import { NotificationType } from '@api/READ_ONLY/notifications_api/Api';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiCheckCircle, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import {
  VisitBillingSyncBatchCompletedNotificationType,
  VisitCompleteBatchCompletedNotificationType,
  VisitDeleteBatchCompletedNotificationType,
} from '../../types/notifications';
import { LIST_ITEM_ANIMATIONS } from '../consts/motionAnimations';

type VisitBatchWidgetNotificationCardProps = {
  notificationDetails:
    | VisitDeleteBatchCompletedNotificationType
    | VisitBillingSyncBatchCompletedNotificationType
    | VisitCompleteBatchCompletedNotificationType;
  onClick: (id: number) => void;
};

const VisitBatchWidgetNotificationCard = ({ notificationDetails, onClick }: VisitBatchWidgetNotificationCardProps) => {
  const { t } = useTranslation();
  const isDeleteBatch = notificationDetails.type === NotificationType.VisitDeleteBatchCompleted;
  const isBillingBatch = notificationDetails.type === NotificationType.VisitBillingSyncBatchCompleted;
  const isCompleteBatch = notificationDetails.type === NotificationType.VisitCompleteBatchCompleted;

  const deleteMeta = isDeleteBatch ? notificationDetails.metadata?.[0] : undefined;
  const billingSyncMeta = isBillingBatch ? notificationDetails.metadata?.[0] : undefined;
  const completeMeta = isCompleteBatch ? notificationDetails.metadata?.[0] : undefined;
  const timeAgo = (iso: string) => dayjs(iso).fromNow();

  const iconWrapperClass = isDeleteBatch
    ? 'bg-amber-100 text-amber-700'
    : isCompleteBatch
      ? 'bg-emerald-100 text-emerald-700'
      : 'bg-cyan-100 text-cyan-700';

  const icon = isDeleteBatch ? (
    <FiTrash2 fontSize={16} />
  ) : isCompleteBatch ? (
    <FiCheckCircle fontSize={16} />
  ) : (
    <FiRefreshCw fontSize={16} />
  );

  const tagColor = isDeleteBatch ? 'gold' : isCompleteBatch ? 'green' : 'cyan';
  const tagLabel = isDeleteBatch
    ? t('notifications-tag-delete-batch')
    : isCompleteBatch
      ? t('notifications-tag-complete-batch')
      : t('notifications-tag-update-billing');

  const summaryLabel = isDeleteBatch
    ? t('notifications-batch-summary-deleted', {
        deleted: deleteMeta?.deletedCount ?? 0,
        requested: deleteMeta?.requestedCount ?? 0,
      })
    : isCompleteBatch
      ? t('notifications-batch-summary-completed', {
          completed: completeMeta?.completedCount ?? 0,
          requested: completeMeta?.requestedCount ?? 0,
        })
      : t('notifications-batch-summary-synced', {
          synced: billingSyncMeta?.syncedCount ?? 0,
          requested: billingSyncMeta?.requestedCount ?? 0,
        });

  return (
    <motion.li key={notificationDetails.notificationId} className="overflow-hidden" {...LIST_ITEM_ANIMATIONS}>
      <div className="group flex flex-col px-4 py-3 hover:bg-slate-50 cursor-pointer">
        <div
          onClick={() => onClick(notificationDetails.notificationId)}
          className="flex w-full items-start gap-3 text-left"
        >
          <div className={`flex h-8 w-8 items-center justify-center rounded-full ${iconWrapperClass}`}>{icon}</div>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-700 line-clamp-2">{notificationDetails.title}</h3>
                <Tag bordered={false} color={tagColor}>
                  <span className="font-light">{tagLabel}</span>
                </Tag>
              </div>

              <span className="ml-2 shrink-0 text-xs text-slate-400">{timeAgo(notificationDetails.createdAt)}</span>
            </div>

            <p className="mt-0.5 line-clamp-3 text-xs text-slate-600">{notificationDetails.message}</p>
            <p className="mt-1 text-xs font-medium text-slate-500">{summaryLabel}</p>
          </div>

          {!notificationDetails.isRead && <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-blue-500" />}
        </div>
      </div>
    </motion.li>
  );
};

export default VisitBatchWidgetNotificationCard;
